

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { useModule } from '../../../context/ModuleContext';
import BottomNav from '../../../components/layout/BottomNav';
import ModuleStrip from '../../../components/layout/ModuleStrip';

const LIGHT = '#EAF3DE';

const CATEGORIES = [
  { key:'vegetables', label:'Vegetables',     sub:'Fresh & Organic',     icon:'/icons/vegetables.png',      emoji:'🥦' },
  { key:'flowers',    label:'Flowering ', sub:'Bloom Your Garden', icon:'/icons/flowers.png',     emoji:'🌸' },
  { key:'fruits',     label:'Fruits ',  sub:'Grow Your Own',      icon:'/icons/fruits.png',      emoji:'🍎' },
  { key:'herbs',      label:'Herbs',          sub:'Pure & Healthy',     icon:'/icons/seeds.png',       emoji:'' },
  { key:'indoor',     label:'Indoor Plants',  sub:'Decorate Naturally', icon:'/icons/outdoor.png',     emoji:'' },
  { key:'pots',       label:'Pots',           sub:'Style Your Space',   icon:'/icons/pots.png',       emoji:'🏺' },
  { key:'seeds',      label:'Seeds ',  sub:'All You Need',       icon:'/icons/fertilizers.png', emoji:'' },
  // { key:'tools',      label:'Tools',          sub:'Garden Ready',       icon:'/icons/tools.png',       emoji:'🛠️' },
];

const BANNERS = [
  { img:'/images/nursery-banner1.jpg', btn:'Shop Plants →', to:'/nursery/list' },
  { img:'/images/nursery-banner2.jpg', btn:'Shop Now →',    to:'/nursery/list' },
  { img:'/images/nursery-banner3.jpg', btn:'Explore →',     to:'/nursery/list' },
];

export default function NurseryHomePage() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { activeModule, switchModule } = useModule();
  const G = activeModule?.color || '#1A6B3C'; // single source of truth: modules/registry.js
  const [nurseries, setNurseries] = useState([]);
  const [plants,    setPlants]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [banner,    setBanner]    = useState(0);
  const bannerTimer = useRef();
  const catScrollRef = useRef();
  const scrollCats = (dir) => {
    catScrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  // Get user city from any possible field
  const userCity = user?.city
    || user?.location?.city
    || user?.locationAddress?.split(',')[0]?.trim()
    || user?.address?.city
    || '';

  const userLat = user?.location?.lat || user?.lat || null;
  const userLng = user?.location?.lng || user?.lng || null;

  useEffect(() => {
    switchModule('nursery');
  }, []); 

  useEffect(() => {
    loadData();
    bannerTimer.current = setInterval(() => setBanner(b => (b + 1) % BANNERS.length), 4000);
    return () => clearInterval(bannerTimer.current);
  }, [user]); // reload when user loads

  const loadData = async () => {
    setLoading(true);

    // Load nurseries — try with location params first, then fall back to
    // an unfiltered query if that returns nothing (e.g. user's city/coords
    // don't exactly match a nursery's stored city, which was hiding real
    // nurseries and showing the empty state even though data existed).
    try {
      let params = 'limit=10&isActive=true';
      const hasLocationFilter = !!((userLat && userLng) || userCity);
      if (userLat && userLng) {
        params += `&lat=${userLat}&lng=${userLng}`;
      } else if (userCity) {
        params += `&city=${encodeURIComponent(userCity)}`;
      }

      let res = await api.get(`/nursery?${params}`);
      let list = res.data.nurseries || res.data.data || res.data || [];

      if (list.length === 0 && hasLocationFilter) {
        // Retry without the location filter so real nurseries still show up.
        res = await api.get('/nursery?limit=10&isActive=true');
        list = res.data.nurseries || res.data.data || res.data || [];
      }

      setNurseries(list);
    } catch { setNurseries([]); }

    // Load popular plants
    try {
      const res = await api.get('/nursery/popular-plants?limit=10');
      const fetchedPlants = res.data.plants || res.data.data || [];
      setPlants(fetchedPlants);
    } catch (err) {
      setPlants([]);
    }

    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate('/nursery/list?search=' + encodeURIComponent(search.trim()));
  };

  // Real API data only — no static/demo fallback
  const displayNurseries = nurseries;

  return (
    <div style={{ background:'#F5F7FA', minHeight:'40vh', paddingBottom:30, fontFamily:'Inter,system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ background:G, padding:2 }}>
        <ModuleStrip />

        <style>{`
  .search-input::placeholder {
    color: rgba(255,255,255,0.8);
    opacity: 1;
  }

  .search-input::-webkit-input-placeholder {
    color: rgba(255,255,255,0.8);
  }

  .search-input::-moz-placeholder {
    color: rgba(255,255,255,0.8);
  }
`}</style>

        <form onSubmit={handleSearch} style={{ margin:'2px 18px 2px 12px' }}>
          <div
            style={{
              flex: 1,
              background: "rgba(255,255,255,.18)",
              borderRadius: 20,
              padding: "6px 15px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid rgba(255,255,255,.25)"
            }}
          >
            <span style={{ fontSize: 12 }}>🔍</span>

            <input
              className="search-input"
              type="text"
              placeholder="Search plants, seeds, pots..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                fontSize: 13,
                color: "#fff",
                background: "transparent",
                boxShadow: "none",
                caretColor: "#fff"
              }}
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: 18,
                  cursor: "pointer",
                  padding: 0
                }}
              >
                ×
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={{ padding:'5px 8px 0' }}>

        {/* Hero Banner — real image */}
        <div style={{ borderRadius:20, overflow:'hidden', marginBottom:20, position:'relative', height:170, cursor:'pointer' }}
          onClick={() => navigate(BANNERS[banner].to || '/nursery/list')}>
          {/* Background image */}
          <img
            src={BANNERS[banner].img}
            alt="banner"
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
            onError={e => {
              e.target.style.display='none';
              e.target.parentNode.style.background='linear-gradient(135deg,#1A6B3C 0%,#2d9e5f 60%,#56c47e 100%)';
            }}
          />
          {/* Dark overlay for text readability */}
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,rgba(0,0,0,.45) 0%,rgba(0,0,0,.1) 70%,rgba(0,0,0,0) 100%)' }} />
          {/* Dots */}
          <div style={{ position:'absolute', bottom:20, right:14, display:'flex', gap:5 }}>
            {BANNERS.map((_, i) => (
              <div key={i} onClick={e => { e.stopPropagation(); setBanner(i); }}
                style={{ width:i===banner?16:6, height:6, borderRadius:3, background:i===banner?'white':'rgba(255,255,255,.5)', cursor:'pointer', transition:'all .3s' }} />
            ))}
          </div>
        </div>

        {/* Shop by Category */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <span style={{ fontSize:16, fontWeight:800, color:'#1a1a1a' }}>Category</span>
          <button onClick={() => navigate('/nursery/list')} style={{ background:'none', border:'none', color:G, fontSize:12, fontWeight:700, cursor:'pointer' }}>View all →</button>
        </div>
        <div style={{ position:'relative', marginBottom:16 }}>
          <button onClick={() => scrollCats(-1)}
            style={{ position:'absolute', left:0, top:'50%', transform:'translateY(-50%)', zIndex:2, width:24, height:24, borderRadius:'50%', background:G, color:'white', border:'2px solid white', boxShadow:'0 2px 6px rgba(0,0,0,.18)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, cursor:'pointer' }}>
            ←
          </button>
          <button onClick={() => scrollCats(1)}
            style={{ position:'absolute', right:0, top:'50%', transform:'translateY(-50%)', zIndex:2, width:24, height:24, borderRadius:'50%', background:G, color:'white', border:'2px solid white', boxShadow:'0 2px 6px rgba(0,0,0,.18)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, cursor:'pointer' }}>
            →
          </button>

          <div ref={catScrollRef} style={{ display:'flex', gap:8, overflowX:'auto', scrollSnapType:'x mandatory', scrollbarWidth:'none', padding:'4px 30px' }}>
            {CATEGORIES.map(cat => (
              <div key={cat.key} onClick={() => navigate('/nursery/list?specialty=' + cat.key)}
                style={{
                  scrollSnapAlign:'start', flexShrink:0, width:104,
                  background:'white', borderRadius:14, boxShadow:'0 3px 10px rgba(0,0,0,.06)',
                  padding:'10px 6px 9px', textAlign:'center', cursor:'pointer', boxSizing:'border-box',
                }}>
                <div style={{
                  width:90, height:78, borderRadius:'50%', background:LIGHT,
                  margin:'0 auto 7px', display:'flex', alignItems:'center', justifyContent:'center',
                  overflow:'hidden', position:'relative',
                }}>
                  <img
                    src={cat.icon}
                    alt={cat.label}
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                    onError={e => { e.target.style.display='none'; }}
                  />
                </div>
                <div style={{ fontSize:10.5, fontWeight:800, color:'#1a1a1a', lineHeight:1.2 }}>{cat.label}</div>
                <div style={{ fontSize:8.5, color:'#9CA3AF', marginTop:2 }}>{cat.sub}</div>
                <div style={{ width:16, height:2.5, background:G, borderRadius:2, margin:'6px auto 0' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Nurseries Near You */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <span style={{ fontSize:16, fontWeight:800, color:'#1a1a1a' }}> Nurseries near you</span>
          <button onClick={() => navigate('/nursery/list')} style={{ background:'none', border:'none', color:G, fontSize:12, fontWeight:700, cursor:'pointer' }}>View all →</button>
        </div>

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'20px 0', marginBottom:14 }}>
            <div className="spinner" style={{ width:36, height:36 }} />
          </div>
        ) : displayNurseries.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#9CA3AF', marginBottom:20 }}>
            <div style={{ fontSize:40, marginBottom:8 }}></div>
            <div style={{ fontSize:13 }}>No nurseries found near you yet</div>
          </div>
        ) : (
          <div style={{ display:'grid',  gap:12, marginBottom:20 }}>
            {displayNurseries.slice(0,4).map(n => (
              <div key={n._id}
                onClick={() => navigate(`/nursery/${n._id}`)}
                style={{ background:'white', borderRadius:16, overflow:'hidden', border:'0.5px solid #E5E7EB', cursor:'pointer' }}>
                <div style={{ height:90, background:LIGHT, position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {(n.photo || n.image || n.coverImage)
                    ? <img src={n.photo || n.image || n.coverImage} alt="" style={{ width:'300%', height:'200%', objectFit:'cover' }} />
                    : <span style={{ fontSize:40 }}></span>}
                  {(n.isVerified || n.verified) && (
                    <div style={{ position:'absolute', top:8, left:8, background:G, color:'white', fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:20 }}>✓ Verified</div>
                  )}
                </div>
                <div style={{ padding:'50px 10px 12px' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>{n.name}</div>
                  <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:8, display:'flex', alignItems:'center', gap:4 }}>
                    <span></span>{n.city || n.location?.city || n.address?.city || 'Nearby'}
                  </div>
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                    {(n.specialties||n.categories||[]).slice(0,2).map(s => (
                      <span key={s} style={{ background:'#FEF9C3', color:'#854D0E', fontSize:9, fontWeight:600, padding:'2px 8px', borderRadius:10, textTransform:'capitalize' }}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Shortcuts */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:20 }}>
          {[].map(item => (
            <div key={item.label} onClick={() => navigate(item.to)}
              style={{ background:'white', borderRadius:14, padding:'16px 10px', display:'flex', flexDirection:'column', alignItems:'center', gap:8, cursor:'pointer', border:'0.5px solid #E5E7EB' }}>
              <span style={{ fontSize:26 }}>{item.icon}</span>
              <span style={{ fontSize:11, color:'#374151', fontWeight:600, textAlign:'center' }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Promo */}
        <div style={{ background:`linear-gradient(135deg,#1B5E20,${G})`, borderRadius:14, padding:'14px 16px', marginBottom:14, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'relative', zIndex:1 }}>
            <div style={{ fontSize:15, fontWeight:800, color:'white', marginBottom:2 }}>Get 20% OFF</div>
            <div style={{ fontSize:11, color:'#A5D6A7', marginBottom:5 }}>on your first order</div>
            <div style={{ fontSize:10, color:'#C8E6C9' }}>Use code: <span style={{ fontWeight:700, color:'#FFEB3B' }}>GREEN20</span></div>
          </div>
          <div style={{ position:'absolute', right:-4, bottom:-8, fontSize:52, opacity:.2 }}></div>
          <div style={{ position:'absolute', right:30, top:6, fontSize:32, opacity:.3 }}></div>
        </div>

      </div>

      <BottomNav />
    </div>
  );
}